import type { AvatarHeight } from "./types";

export const CELL_SIZE = 7;
export const SHEET_WIDTH = 64; // actual image width in px
export const SHEET_HEIGHT = 64; // actual image height in px
export const RENDER_SCALE = 4;

/** Snap a value to the nearest sprite pixel grid (multiple of RENDER_SCALE). */
export function snapToGrid(x: number): number {
  return Math.round(x / RENDER_SCALE) * RENDER_SCALE;
}
export const FRAME_DURATION_MS = 100;
export const EMOTE_DURATION_MS = 2000;
export const HAND_DURATION_MS = 2000;
export const JUMP_BODY_COL = 2; // frame[2] of body row
export const JUMP_Y_OFFSETS = [3, 4, 3] as const;
export const JUMP_TRAVEL_MULTIPLIER = 2;
export const JUMP_MOMENTUM_DISTANCE = 60; // rendered px ahead to target on jump
export const SPAWN_MAX_WIDTH = 360;

// Spritesheet row indices
export const SpriteRow = {
  TallRun: 0,
  MediumRun: 1,
  ShortRun: 2,
  Staff: 3,
  Hats: 4,
  Items: 5,
} as const;

// Item frame columns in row 5
export const ItemFrame = {
  SwordVert: 0,
  Shield: 2,
  Heart: 3,
  Bubble: 4,
} as const;

// Which row to use for each body height
export const RUN_BODY_ROW: Record<AvatarHeight, number> = {
  tall: SpriteRow.TallRun,
  medium: SpriteRow.MediumRun,
  short: SpriteRow.ShortRun,
};

// Pixel travel distance per run frame [frame1, frame2, frame3, frame4]
export const TRAVEL_PER_FRAME: Record<AvatarHeight, [number, number, number, number]> = {
  short: [1, 2, 2, 2],
  medium: [1, 2, 2, 2],
  tall: [2, 3, 3, 3],
};

// Standing-to-run transition offset (1px in movement direction)
export const STANDING_OFFSET = 1;

// Keyboard carrot: targetX placed this far ahead of simulatedX while arrow held
// ~3 frames of medium travel × RENDER_SCALE = 2+2+2 = 6 × 4 = 24px
export const CARROT_DISTANCE = 24;

// Staff-run animation: 8-frame cycle spanning 2 run loops
// These are column indices into row 3 (Staff row)
export const STAFF_RUN_FRAMES = [3, 2, 3, 4, 3, 2, 3, 4] as const;

// Which staff cycle frames get an additional scaleX(-1) mirror
export const STAFF_MIRROR_MASK = [
  false, false, false, false,
  true, true, true, false,
] as const;

// Standing staff column per height (row 3)
// Short avatar uses the short-staff sprite (col 1)
export const STANDING_STAFF_COL: Record<AvatarHeight, number> = {
  tall: 0,
  medium: 0,
  short: 1,
};

// Staff/sword per-frame offsets during run (8-frame cycle)
// These are added to the base WEAPON_OFFSET for each staff cycle frame
export const STAFF_RUN_OFFSETS = [
  { x: 4, y: 3 },
  { x: 5, y: 3 },
  { x: 4, y: 3 },
  { x: 1, y: 3 },
  { x: -4, y: 3 },
  { x: -5, y: 3 },
  { x: -4, y: 3 },
  { x: 1, y: 3 },
] as const;

// Shield per-frame offsets during run (8-frame cycle, same for all heights)
export const SHIELD_RUN_OFFSETS = [
  { x: 0, y: 1 },
  { x: 0, y: 2 },
  { x: 0, y: 1 },
  { x: 1, y: 1 },
  { x: 2, y: 1 },
  { x: 3, y: 1 },
  { x: 2, y: 1 },
  { x: 1, y: 1 },
] as const;

// Hat y-offset per height (x is always 0, hats bottom-aligned in cell)
export const HAT_Y_OFFSET: Record<AvatarHeight, number> = {
  short: 4,
  medium: 5,
  tall: 6,
};

// Bubble emote Y-offset per height (in sprite pixels from bottom of cell)
export const BUBBLE_Y_OFFSET: Record<AvatarHeight, number> = {
  short: 2,
  medium: 3,
  tall: 4,
};

// Weapon base offsets (in sprite pixels, scaled at render time)
export const WEAPON_OFFSET = {
  staff: { x: 5, y: 0 },
  sword: { x: 5, y: 2 },
  shield: { x: 0, y: 1 },
} as const;
